import React, { useState, useEffect } from 'react';
import { authFetch } from '../lib/authFetch';
import { Building2, Droplet, Rocket, CreditCard, Gem } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${(v ?? 0).toLocaleString()}`;
};

// Canonical fund codes from Prompt 3 seed: PROP / LIQ / PEVC / CRED / CMDTY
const FUNDS = [
  {
    code: 'PROP',
    name: 'Property Fund',
    desc: 'Commercial real estate holdings',
    icon: Building2,
    accent: 'amber',
    tabActive: 'data-[state=active]:bg-amber-500',
    iconColor: 'text-amber-600',
    badge: 'bg-amber-50',
  },
  {
    code: 'LIQ',
    name: 'Liquid Fund',
    desc: 'System liquidity buffer — Tier 1 cash, T-bills, listed equities & futures',
    icon: Droplet,
    accent: 'sky',
    tabActive: 'data-[state=active]:bg-sky-500',
    iconColor: 'text-sky-600',
    badge: 'bg-sky-50',
  },
  {
    code: 'PEVC',
    name: 'PE / VC Fund',
    desc: 'Private equity & venture capital co-investments',
    icon: Rocket,
    accent: 'purple',
    tabActive: 'data-[state=active]:bg-purple-500',
    iconColor: 'text-purple-600',
    badge: 'bg-purple-50',
  },
  {
    code: 'CRED',
    name: 'Credit Fund',
    desc: 'Private credit, CLOs and structured products',
    icon: CreditCard,
    accent: 'emerald',
    tabActive: 'data-[state=active]:bg-emerald-500',
    iconColor: 'text-emerald-600',
    badge: 'bg-emerald-50',
  },
  {
    code: 'CMDTY',
    name: 'Commodities Fund',
    desc: 'Gold, precious metals and broad commodity exposures',
    icon: Gem,
    accent: 'orange',
    tabActive: 'data-[state=active]:bg-orange-500',
    iconColor: 'text-orange-600',
    badge: 'bg-orange-50',
  },
];

const FundBadge = ({ code, accent }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-${accent}-100 text-${accent}-700`}
    data-testid={`fund-badge-${code}`}
  >
    {code}
  </span>
);

const AssetTable = ({ assets, fundMeta }) => {
  if (!assets.length) {
    return (
      <div className="p-8 text-center text-sm text-gray-500">
        No assets seeded for {fundMeta.name} yet.
      </div>
    );
  }
  return (
    <Table data-testid={`asset-table-${fundMeta.code}`}>
      <TableHeader>
        <TableRow>
          <TableHead>Asset</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Wrapper</TableHead>
          <TableHead>Custody</TableHead>
          {fundMeta.code === 'LIQ' && <TableHead>Tier</TableHead>}
          <TableHead>Rating</TableHead>
          <TableHead className="text-right">Cost Basis</TableHead>
          <TableHead className="text-right">Current Value</TableHead>
          <TableHead className="text-right">Unrealized</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {assets.map((a) => (
          <TableRow key={a.asset_id}>
            <TableCell>
              <p className="font-medium text-gray-900">{a.asset_name}</p>
              {a.sub_strategy && (
                <p className="text-xs text-gray-500">{a.sub_strategy}</p>
              )}
            </TableCell>
            <TableCell>
              <Badge variant="outline" className={fundMeta.badge}>
                {(a.asset_type || '').replace(/_/g, ' ')}
              </Badge>
            </TableCell>
            <TableCell className="text-sm text-gray-600">
              {a.wrapper_type ? a.wrapper_type.replace(/_/g, ' ') : '—'}
            </TableCell>
            <TableCell className="text-sm text-gray-600">
              {a.custody_location ? a.custody_location.replace(/_/g, ' ') : '—'}
            </TableCell>
            {fundMeta.code === 'LIQ' && (
              <TableCell className="text-xs text-gray-500">
                {a.tier ? a.tier.replace(/_/g, ' ') : '—'}
              </TableCell>
            )}
            <TableCell data-testid={`asset-rating-${a.asset_id}`}>
              {a.credit_rating ? (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${
                  a.credit_rating.startsWith('AAA') ? 'bg-emerald-100 text-emerald-700' :
                  a.credit_rating.startsWith('AA') ? 'bg-teal-100 text-teal-700' :
                  a.credit_rating.startsWith('A') ? 'bg-sky-100 text-sky-700' :
                  a.credit_rating.startsWith('BBB') ? 'bg-amber-100 text-amber-700' :
                  'bg-orange-100 text-orange-700'
                }`}>
                  {a.credit_rating}
                </span>
              ) : (
                <span className="text-xs text-gray-400">—</span>
              )}
            </TableCell>
            <TableCell className="text-right">{fmt(a.purchase_price)}</TableCell>
            <TableCell className="text-right font-semibold">
              {fmt(a.current_value)}
            </TableCell>
            <TableCell
              className={`text-right font-medium ${
                a.unrealized_gain >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {a.unrealized_gain >= 0 ? '+' : ''}{fmt(a.unrealized_gain)}
              <span className="text-xs ml-1">
                ({a.unrealized_gain_pct?.toFixed(1)}%)
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default function AssetLedgerPage() {
  const [fundSummary, setFundSummary] = useState([]);
  const [allAssets, setAllAssets] = useState([]);
  const [selectedFund, setSelectedFund] = useState('ALL');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [summaryRes, assetsRes] = await Promise.all([
          authFetch(`${API_BASE}/api/lms/assets/summary`),
          authFetch(`${API_BASE}/api/lms/assets`),
        ]);
        if (summaryRes.ok) setFundSummary(await summaryRes.json());
        if (assetsRes.ok) setAllAssets(await assetsRes.json());
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading)
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="animate-spin w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full"
          data-testid="ledger-loading"
        />
      </div>
    );

  const summaryByCode = Object.fromEntries(
    fundSummary.map((f) => [f._id, f]),
  );
  const totalValue = fundSummary.reduce(
    (s, f) => s + (f.total_current_value || 0),
    0,
  );
  const totalCost = fundSummary.reduce(
    (s, f) => s + (f.total_purchase_price || 0),
    0,
  );
  const totalGain = totalValue - totalCost;
  const totalAssets = fundSummary.reduce(
    (s, f) => s + (f.total_assets || 0),
    0,
  );

  return (
    <div className="p-6 space-y-6" data-testid="asset-ledger-page">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Asset Ledger</h1>
        <p className="text-sm text-gray-500 mt-1">
          Underlying assets held in each of the five wholesale funds (Standard Preamble v3)
        </p>
      </div>

      {/* Fund Summary Cards — 5 wholesale funds + total */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {FUNDS.map((f) => {
          const data = summaryByCode[f.code] || {
            total_current_value: 0,
            total_assets: 0,
            unrealized_gain: 0,
            unrealized_gain_pct: 0,
          };
          const Icon = f.icon;
          return (
            <Card
              key={f.code}
              data-testid={`fund-card-${f.code}`}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedFund === f.code ? 'ring-2 ring-indigo-500' : ''
              }`}
              onClick={() =>
                setSelectedFund(selectedFund === f.code ? 'ALL' : f.code)
              }
            >
              <CardContent className="pt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${f.iconColor}`} />
                  <FundBadge code={f.code} accent={f.accent} />
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {fmt(data.total_current_value)}
                </p>
                <p className="text-xs text-gray-500">
                  {data.total_assets} assets
                </p>
                <p
                  className={`text-xs mt-1 ${
                    data.unrealized_gain >= 0
                      ? 'text-emerald-600'
                      : 'text-red-600'
                  }`}
                >
                  {data.unrealized_gain >= 0 ? '+' : ''}
                  {(data.unrealized_gain_pct || 0).toFixed(1)}% unrealized
                </p>
              </CardContent>
            </Card>
          );
        })}
        <Card
          className={`cursor-pointer transition-all ${
            selectedFund === 'ALL' ? 'ring-2 ring-indigo-500' : ''
          }`}
          onClick={() => setSelectedFund('ALL')}
          data-testid="fund-card-total"
        >
          <CardContent className="pt-4">
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              Total
            </p>
            <p className="text-xl font-bold text-indigo-600">{fmt(totalValue)}</p>
            <p className="text-xs text-gray-500">{totalAssets} assets</p>
            <p
              className={`text-xs mt-1 ${
                totalGain >= 0 ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {totalGain >= 0 ? '+' : ''}
              {totalCost
                ? ((totalGain / totalCost) * 100).toFixed(1)
                : '0.0'}
              % gain
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Asset Details by Fund — 5 tabs */}
      <Tabs defaultValue="PROP" className="space-y-4">
        <TabsList
          className="bg-gray-100 p-1 rounded-lg grid grid-cols-5 gap-1 w-full max-w-4xl"
          data-testid="ledger-fund-tabs"
        >
          {FUNDS.map((f) => {
            const Icon = f.icon;
            const count = allAssets.filter((a) => a.fund_code === f.code).length;
            return (
              <TabsTrigger
                key={f.code}
                value={f.code}
                data-testid={`ledger-tab-${f.code}`}
                className={`flex items-center justify-center gap-2 py-3 px-3 rounded-md ${f.tabActive} data-[state=active]:text-white data-[state=active]:shadow-sm`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-semibold text-sm">{f.name}</span>
                <span className="text-xs opacity-80">({count})</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {FUNDS.map((f) => {
          const Icon = f.icon;
          const assets = allAssets.filter((a) => a.fund_code === f.code);
          return (
            <TabsContent key={f.code} value={f.code}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${f.iconColor}`} />
                    {f.name}
                  </CardTitle>
                  <CardDescription>{f.desc}</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  <AssetTable assets={assets} fundMeta={f} />
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
